import { useTranslation } from 'react-i18next';
import { MousePointerClick, TrendingUp, Globe, Smartphone, Target, DollarSign } from 'lucide-react';
import { Card, Badge } from '@/components/ui';

interface StatsOverviewProps {
  totalClicks: number;
  clicksThisWeek: number;
  weeklyChange: number;
  countries: number;
  mobilePercentage: number;
  totalConversions?: number;
  conversionRate?: number;
  totalRevenue?: number;
}

const StatsOverview = ({
  totalClicks,
  clicksThisWeek,
  weeklyChange,
  countries,
  mobilePercentage,
  totalConversions,
  conversionRate,
  totalRevenue,
}: StatsOverviewProps) => {
  const { t, i18n } = useTranslation();

  const stats: { label: string; value: string; change?: number; icon: React.ReactNode; color: string }[] = [
    {
      label: t('stats.totalClicks'),
      value: totalClicks.toLocaleString(i18n.language),
      icon: <MousePointerClick className="w-5 h-5" />,
      color: 'bg-lime-100 text-lime-600',
    },
    {
      label: t('stats.thisWeek'),
      value: clicksThisWeek.toLocaleString(i18n.language),
      change: weeklyChange,
      icon: <TrendingUp className="w-5 h-5" />,
      color: 'bg-lavender-100 text-lavender-600',
    },
    {
      label: t('stats.countries'),
      value: countries.toString(),
      icon: <Globe className="w-5 h-5" />,
      color: 'bg-blue-100 text-blue-600',
    },
    {
      label: t('stats.mobile'),
      value: `${mobilePercentage}%`,
      icon: <Smartphone className="w-5 h-5" />,
      color: 'bg-green-100 text-green-600',
    },
  ];

  if (totalConversions != null && totalConversions > 0) {
    stats.push({
      label: t('pixel.totalConversions'),
      value: totalConversions.toLocaleString(i18n.language),
      icon: <Target className="w-5 h-5" />,
      color: 'bg-purple-100 text-purple-600',
    });
    if (conversionRate != null) {
      stats.push({
        label: t('pixel.conversionRate'),
        value: `${conversionRate}%`,
        icon: <TrendingUp className="w-5 h-5" />,
        color: 'bg-orange-100 text-orange-600',
      });
    }
    if (totalRevenue != null && totalRevenue > 0) {
      stats.push({
        label: t('pixel.totalRevenue'),
        value: new Intl.NumberFormat(i18n.language, { style: 'currency', currency: 'EUR' }).format(totalRevenue),
        icon: <DollarSign className="w-5 h-5" />,
        color: 'bg-green-100 text-green-600',
      });
    }
  }

  return (
    <div className={`grid grid-cols-2 ${stats.length > 4 ? 'lg:grid-cols-3 xl:grid-cols-4' : 'lg:grid-cols-4'} gap-4`}>
      {stats.map((stat, index) => (
        <Card key={index} variant="default" padding="md">
          <div className="flex items-center justify-between mb-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${stat.color}`}>
              {stat.icon}
            </div>
            {stat.change !== undefined && (
              <Badge
                variant={stat.change >= 0 ? 'lime' : 'error'}
                size="sm"
              >
                {stat.change >= 0 ? '+' : ''}{stat.change}%
              </Badge>
            )}
          </div>
          <p className="text-2xl font-bold text-dark-800 dark:text-white">{stat.value}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">{stat.label}</p>
        </Card>
      ))}
    </div>
  );
};

export default StatsOverview;
